/**
 * Discovery Browse URL search-param SSOT (V1A Home Wave 2).
 *
 * Owned keys: q, entity, listKind, platformCategory, radius (meters), sort, view,
 * intentType, intentKey (legacy — sanitize only; never write from new UI).
 * Cursor is ephemeral (React Query / memory) — never serialized.
 * User GPS / map center never belong in the URL — session or component state only.
 *
 * `favoritesOnly` is legacy: sanitize remaps truthy → view=favorites and deletes the flag
 * (mutual exclusion — view=favorites wins). Never write favoritesOnly from new UI.
 */

export const DISCOVERY_SORTS = ['relevance', 'distance'] as const;
export const DISCOVERY_VIEWS = ['list', 'map', 'favorites', 'bag'] as const;
export const DISCOVERY_ENTITIES = ['all', 'product', 'shop', 'tenant'] as const;
export const DISCOVERY_LIST_KINDS = ['salesPoint', 'tenant'] as const;

export type DiscoverySort = (typeof DISCOVERY_SORTS)[number];
export type DiscoveryView = (typeof DISCOVERY_VIEWS)[number];
export type DiscoveryEntity = (typeof DISCOVERY_ENTITIES)[number];
export type DiscoveryListKind = (typeof DISCOVERY_LIST_KINDS)[number];

export const DISCOVERY_PARAM_Q = 'q';
export const DISCOVERY_PARAM_ENTITY = 'entity';
export const DISCOVERY_PARAM_LIST_KIND = 'listKind';
export const DISCOVERY_PARAM_PLATFORM_CATEGORY = 'platformCategory';
export const DISCOVERY_PARAM_RADIUS = 'radius';
export const DISCOVERY_PARAM_SORT = 'sort';
export const DISCOVERY_PARAM_VIEW = 'view';
/** Legacy ★ offer filter — sanitize → view=favorites; never serialize. */
export const DISCOVERY_PARAM_FAVORITES_ONLY = 'favoritesOnly';
/** C1 §2.2 — two-step search intent (never GPS). Legacy: sanitize / map only. */
export const DISCOVERY_PARAM_INTENT_TYPE = 'intentType';
export const DISCOVERY_PARAM_INTENT_KEY = 'intentKey';
/** Ephemeral pagination — drop from URL; do not round-trip. */
export const DISCOVERY_PARAM_CURSOR = 'cursor';

export const DISCOVERY_OWNED_PARAM_KEYS = [
  DISCOVERY_PARAM_Q,
  DISCOVERY_PARAM_ENTITY,
  DISCOVERY_PARAM_LIST_KIND,
  DISCOVERY_PARAM_PLATFORM_CATEGORY,
  DISCOVERY_PARAM_RADIUS,
  DISCOVERY_PARAM_SORT,
  DISCOVERY_PARAM_VIEW,
  DISCOVERY_PARAM_FAVORITES_ONLY,
  DISCOVERY_PARAM_INTENT_TYPE,
  DISCOVERY_PARAM_INTENT_KEY,
] as const;

export type DiscoveryOwnedParamKey = (typeof DISCOVERY_OWNED_PARAM_KEYS)[number];

/** Must stay aligned with backend publicDiscoverySchemas radius bounds. */
export const DISCOVERY_RADIUS_METERS_MIN = 1;
export const DISCOVERY_RADIUS_METERS_MAX = 50_000;

export const DISCOVERY_Q_MAX_LENGTH = 200;
export const DISCOVERY_PLATFORM_CATEGORY_MAX_LENGTH = 100;
export const DISCOVERY_INTENT_KEY_MAX_LENGTH = 200;

export const DISCOVERY_INTENT_TYPES = ['productService', 'category', 'tenant'] as const;
export type DiscoveryIntentType = (typeof DISCOVERY_INTENT_TYPES)[number];

export const DISCOVERY_DEFAULT_SORT: DiscoverySort = 'relevance';
export const DISCOVERY_DEFAULT_VIEW: DiscoveryView = 'list';
export const DISCOVERY_DEFAULT_ENTITY: DiscoveryEntity = 'all';
export const DISCOVERY_DEFAULT_LIST_KIND: DiscoveryListKind = 'salesPoint';

export type DiscoverySearchParamsState = {
  readonly q: string;
  readonly entity: DiscoveryEntity;
  /** Seznam nested switch — only meaningful / serialized when view=list. */
  readonly listKind: DiscoveryListKind;
  readonly platformCategory: string | null;
  /** Radius in meters when a location filter is active; omitted from URL otherwise. */
  readonly radiusMeters: number | null;
  readonly sort: DiscoverySort;
  readonly view: DiscoveryView;
  /** C1 §2.2 — selected search intent (requires non-empty q). Legacy deep links. */
  readonly intentType: DiscoveryIntentType | null;
  readonly intentKey: string | null;
};

export const DISCOVERY_SEARCH_PARAMS_DEFAULTS: DiscoverySearchParamsState = {
  q: '',
  entity: DISCOVERY_DEFAULT_ENTITY,
  listKind: DISCOVERY_DEFAULT_LIST_KIND,
  platformCategory: null,
  radiusMeters: null,
  sort: DISCOVERY_DEFAULT_SORT,
  view: DISCOVERY_DEFAULT_VIEW,
  intentType: null,
  intentKey: null,
};

/**
 * D12 / NEW G6 — "search active" for entity chips (Filtry).
 * IFF `q.trim().length > 0 OR entity !== 'all'`. `listKind` never mounts chips.
 * Screen 1 (empty q + entity=all) = no chips.
 */
export function isDiscoverySearchActive(
  state: Pick<DiscoverySearchParamsState, 'q' | 'entity'>,
): boolean {
  return state.q.trim().length > 0 || state.entity !== 'all';
}

/** Screen-1 default Seznam — FR-11 Obchody overview mounts only here. */
export function isDefaultBrowseSeznamScreen(
  state: Pick<DiscoverySearchParamsState, 'q' | 'entity' | 'view' | 'listKind'>,
): boolean {
  return (
    state.q.trim().length === 0 &&
    state.entity === 'all' &&
    state.view === 'list' &&
    state.listKind === 'salesPoint'
  );
}

/**
 * Screen 3 / Wave 2.8 — Seznam Tenanti brand directory.
 * empty-q + entity=all + view=list + listKind=tenant (chips off; never nearby-SP).
 */
export function isBrowseTenantDirectorySurface(
  state: Pick<DiscoverySearchParamsState, 'q' | 'entity' | 'view' | 'listKind'>,
): boolean {
  return (
    state.view === 'list' &&
    state.listKind === 'tenant' &&
    !isDiscoverySearchActive(state)
  );
}

/**
 * Wave 4 / prior-G10-promo — default Nakupovat Seznam Screen-1 promo surface.
 * GPS / nearby / location MUST NOT hide (unlike `isNeutralDiscoverySurface`).
 * Hide on text `q`, category filter, map|favorites|bag, tenant Seznam, non-all entity.
 */
export function isDefaultNakupovatSeznamPromoSurface(
  state: Pick<
    DiscoverySearchParamsState,
    'q' | 'entity' | 'view' | 'listKind' | 'platformCategory'
  >,
): boolean {
  return isDefaultBrowseSeznamScreen(state) && state.platformCategory === null;
}

/**
 * W6 / D22 / HUX-11 / §3.2.14 — collapse landing **search** on Tenanti / Mapa / ☆ / Taška★.
 * Rail+promo already gate via `isDefaultNakupovatSeznamPromoSurface`.
 * Search-active Provozovny (and search-active tenant entity chips) keep search visible.
 */
export function isBrowseLandingSearchCollapsed(
  state: Pick<DiscoverySearchParamsState, 'q' | 'entity' | 'view' | 'listKind'>,
): boolean {
  if (state.view === 'map' || state.view === 'favorites' || state.view === 'bag') {
    return true;
  }
  return isBrowseTenantDirectorySurface(state);
}

/**
 * URL keys that must never appear (case-insensitive). Covers GPS, map camera,
 * and common geo aliases so share/copy cannot leak a fix.
 */
const SENSITIVE_GEO_PARAM_KEYS = new Set([
  'lat',
  'lng',
  'lon',
  'long',
  'latitude',
  'longitude',
  'gps',
  'geo',
  'geolocation',
  'coords',
  'coord',
  'coordinate',
  'coordinates',
  'latlng',
  'latlon',
  'll',
  'userlat',
  'userlng',
  'userlon',
  'accuracy',
  'altitude',
  'heading',
  'speed',
  'position',
  'maplat',
  'maplng',
  'center',
  'centerlat',
  'centerlng',
  'clat',
  'clng',
]);

export function isSensitiveGeoParamKey(key: string): boolean {
  return SENSITIVE_GEO_PARAM_KEYS.has(key.trim().toLowerCase());
}

export function isDiscoveryOwnedParamKey(key: string): boolean {
  return (DISCOVERY_OWNED_PARAM_KEYS as readonly string[]).includes(key);
}

export function isDiscoveryEphemeralParamKey(key: string): boolean {
  return key.trim().toLowerCase() === DISCOVERY_PARAM_CURSOR;
}

function stripControlChars(raw: string): string {
  let out = '';
  for (const char of raw) {
    const code = char.charCodeAt(0);
    if (code < 32 || code === 127) {
      continue;
    }
    out += char;
  }
  return out;
}

export function normalizeDiscoveryQuery(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) {
    return '';
  }
  const trimmed = stripControlChars(raw).trim();
  if (trimmed.length === 0) {
    return '';
  }
  return trimmed.slice(0, DISCOVERY_Q_MAX_LENGTH);
}

/**
 * Platform category id (numeric string) or slug. Empty / oversized / control
 * characters → null (drop from URL).
 */
export function normalizeDiscoveryPlatformCategory(
  raw: string | null | undefined,
): string | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  const trimmed = stripControlChars(raw).trim();
  if (trimmed.length === 0) {
    return null;
  }
  if (trimmed.length > DISCOVERY_PLATFORM_CATEGORY_MAX_LENGTH) {
    return null;
  }
  return trimmed;
}

export function parseDiscoverySort(raw: string | null | undefined): DiscoverySort {
  if (raw === null || raw === undefined) {
    return DISCOVERY_DEFAULT_SORT;
  }
  const normalized = raw.trim().toLowerCase();
  if (normalized === 'distance') {
    return 'distance';
  }
  return DISCOVERY_DEFAULT_SORT;
}

export function parseDiscoveryView(raw: string | null | undefined): DiscoveryView {
  if (raw === null || raw === undefined) {
    return DISCOVERY_DEFAULT_VIEW;
  }
  const normalized = raw.trim().toLowerCase();
  if (normalized === 'map') {
    return 'map';
  }
  if (normalized === 'favorites') {
    return 'favorites';
  }
  if (normalized === 'bag') {
    return 'bag';
  }
  return DISCOVERY_DEFAULT_VIEW;
}

export function parseDiscoveryEntity(raw: string | null | undefined): DiscoveryEntity {
  if (raw === null || raw === undefined) {
    return DISCOVERY_DEFAULT_ENTITY;
  }
  const normalized = raw.trim().toLowerCase();
  if (normalized === 'product') {
    return 'product';
  }
  if (normalized === 'shop') {
    return 'shop';
  }
  if (normalized === 'tenant') {
    return 'tenant';
  }
  return DISCOVERY_DEFAULT_ENTITY;
}

export function parseDiscoveryListKind(raw: string | null | undefined): DiscoveryListKind {
  if (raw === null || raw === undefined) {
    return DISCOVERY_DEFAULT_LIST_KIND;
  }
  const normalized = raw.trim().toLowerCase();
  if (normalized === 'tenant') {
    return 'tenant';
  }
  return DISCOVERY_DEFAULT_LIST_KIND;
}

/** Truthy: 1, true, yes (case-insensitive). Default false when absent. Legacy only. */
export function parseDiscoveryFavoritesOnly(raw: string | null | undefined): boolean {
  if (raw === null || raw === undefined) {
    return false;
  }
  const normalized = raw.trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes';
}

export function parseDiscoveryIntentType(
  raw: string | null | undefined,
): DiscoveryIntentType | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  const normalized = raw.trim();
  if (normalized === 'productService') {
    return 'productService';
  }
  if (normalized === 'category') {
    return 'category';
  }
  if (normalized === 'tenant') {
    return 'tenant';
  }
  return null;
}

/**
 * Intent key (productServiceKey, category slug, tenant code). Empty / oversized → null.
 */
export function normalizeDiscoveryIntentKey(
  raw: string | null | undefined,
): string | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  const trimmed = stripControlChars(raw).trim();
  if (trimmed.length === 0) {
    return null;
  }
  if (trimmed.length > DISCOVERY_INTENT_KEY_MAX_LENGTH) {
    return null;
  }
  return trimmed;
}

/**
 * Integer meters only. Invalid / out of range → null (omit; do not clamp into URL).
 */
export function parseDiscoveryRadiusMeters(
  raw: string | null | undefined,
): number | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  const trimmed = raw.trim().replace(/,/g, '.');
  if (trimmed.length === 0) {
    return null;
  }
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const value = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(value) || value < DISCOVERY_RADIUS_METERS_MIN) {
    return null;
  }
  if (value > DISCOVERY_RADIUS_METERS_MAX) {
    return null;
  }
  return value;
}

/**
 * Resolve view with G14 favoritesOnly mutual exclusion:
 * - truthy favoritesOnly alone → view=favorites
 * - both truthy favoritesOnly and view=favorites → view=favorites wins
 * - truthy favoritesOnly with any other view → remap to favorites (legacy ★ = favorites mode)
 * - non-truthy / invalid favoritesOnly → strip only (handled by never serializing)
 */
function resolveDiscoveryViewWithFavoritesOnlyMigration(
  viewRaw: string | null,
  favoritesOnlyRaw: string | null,
): DiscoveryView {
  const favoritesOnlyTruthy = parseDiscoveryFavoritesOnly(favoritesOnlyRaw);
  const parsedView = parseDiscoveryView(viewRaw);
  if (favoritesOnlyTruthy) {
    return 'favorites';
  }
  return parsedView;
}

/**
 * Legacy intent → entity (Wave 2/3 sanitize). Intent* still accepted when q non-empty
 * until Wave 3 fully retires picker writes; entity is derived for deep links.
 */
function resolveEntityFromLegacyIntent(
  entity: DiscoveryEntity,
  intentType: DiscoveryIntentType | null,
  intentKey: string | null,
  platformCategory: string | null,
): {
  readonly entity: DiscoveryEntity;
  readonly platformCategory: string | null;
  readonly intentType: DiscoveryIntentType | null;
  readonly intentKey: string | null;
} {
  if (intentType === null) {
    return { entity, platformCategory, intentType: null, intentKey: null };
  }
  if (intentType === 'productService') {
    return {
      entity: 'product',
      platformCategory,
      intentType: null,
      intentKey: null,
    };
  }
  if (intentType === 'tenant') {
    return {
      entity: 'tenant',
      platformCategory,
      intentType: null,
      intentKey: null,
    };
  }
  if (intentType === 'category') {
    return {
      entity,
      platformCategory:
        platformCategory ?? (intentKey !== null ? intentKey : null),
      intentType: null,
      intentKey: null,
    };
  }
  return { entity, platformCategory, intentType: null, intentKey: null };
}

export function parseDiscoverySearchParams(
  search: URLSearchParams,
): DiscoverySearchParamsState {
  const q = normalizeDiscoveryQuery(search.get(DISCOVERY_PARAM_Q));
  const intentTypeRaw = parseDiscoveryIntentType(search.get(DISCOVERY_PARAM_INTENT_TYPE));
  const intentKeyRaw = normalizeDiscoveryIntentKey(search.get(DISCOVERY_PARAM_INTENT_KEY));
  const intentType = q.length > 0 ? intentTypeRaw : null;
  const intentKey =
    q.length > 0 && intentType !== null && intentKeyRaw !== null ? intentKeyRaw : null;

  const view = resolveDiscoveryViewWithFavoritesOnlyMigration(
    search.get(DISCOVERY_PARAM_VIEW),
    search.get(DISCOVERY_PARAM_FAVORITES_ONLY),
  );

  let entity = parseDiscoveryEntity(search.get(DISCOVERY_PARAM_ENTITY));
  let platformCategory = normalizeDiscoveryPlatformCategory(
    search.get(DISCOVERY_PARAM_PLATFORM_CATEGORY),
  );

  const legacy = resolveEntityFromLegacyIntent(
    entity,
    intentType,
    intentKey,
    platformCategory,
  );
  entity = legacy.entity;
  platformCategory = legacy.platformCategory;

  const listKindRaw = parseDiscoveryListKind(search.get(DISCOVERY_PARAM_LIST_KIND));
  const listKind =
    view === 'list' ? listKindRaw : DISCOVERY_DEFAULT_LIST_KIND;

  return {
    q,
    entity,
    listKind,
    platformCategory,
    radiusMeters: parseDiscoveryRadiusMeters(search.get(DISCOVERY_PARAM_RADIUS)),
    sort: parseDiscoverySort(search.get(DISCOVERY_PARAM_SORT)),
    view,
    intentType: legacy.intentType,
    intentKey: legacy.intentKey,
  };
}

function shouldCopyPreservedKey(key: string): boolean {
  if (isDiscoveryOwnedParamKey(key)) {
    return false;
  }
  if (isDiscoveryEphemeralParamKey(key)) {
    return false;
  }
  if (isSensitiveGeoParamKey(key)) {
    return false;
  }
  return true;
}

export type SerializeDiscoverySearchParamsOptions = {
  /** Copy unrelated keys (utm, returnTo, …). Owned / cursor / GPS keys are never copied. */
  readonly preserveFrom?: URLSearchParams;
  /**
   * When true, emit sort=relevance even though it is the default — user explicitly chose
   * Relevance while nearby radius is active (setSort patch or sort already in URL).
   */
  readonly pinSortRelevance?: boolean;
};

function shouldSerializeSortParam(
  state: DiscoverySearchParamsState,
  options: SerializeDiscoverySearchParamsOptions,
): boolean {
  const sort = parseDiscoverySort(state.sort);
  if (sort !== DISCOVERY_DEFAULT_SORT) {
    return true;
  }
  if (state.radiusMeters === null) {
    return false;
  }
  if (options.pinSortRelevance === true) {
    return true;
  }
  const preserveFrom = options.preserveFrom;
  if (
    preserveFrom?.has(DISCOVERY_PARAM_SORT) &&
    parseDiscoverySort(preserveFrom.get(DISCOVERY_PARAM_SORT)) === 'relevance'
  ) {
    return true;
  }
  return false;
}

/**
 * Writes owned keys (omitting defaults / empty). Never writes cursor, GPS, or favoritesOnly.
 */
export function serializeDiscoverySearchParams(
  state: DiscoverySearchParamsState,
  options: SerializeDiscoverySearchParamsOptions = {},
): URLSearchParams {
  const next = new URLSearchParams();
  const preserveFrom = options.preserveFrom;
  if (preserveFrom !== undefined) {
    for (const [key, value] of preserveFrom.entries()) {
      if (!shouldCopyPreservedKey(key)) {
        continue;
      }
      next.append(key, value);
    }
  }

  const q = normalizeDiscoveryQuery(state.q);
  if (q.length > 0) {
    next.set(DISCOVERY_PARAM_Q, q);
  }

  const entity = parseDiscoveryEntity(state.entity);
  if (entity !== DISCOVERY_DEFAULT_ENTITY) {
    next.set(DISCOVERY_PARAM_ENTITY, entity);
  }

  const view = parseDiscoveryView(state.view);
  if (view !== DISCOVERY_DEFAULT_VIEW) {
    next.set(DISCOVERY_PARAM_VIEW, view);
  }

  if (view === 'list') {
    const listKind = parseDiscoveryListKind(state.listKind);
    if (listKind !== DISCOVERY_DEFAULT_LIST_KIND) {
      next.set(DISCOVERY_PARAM_LIST_KIND, listKind);
    }
  }

  const category = normalizeDiscoveryPlatformCategory(state.platformCategory);
  if (category !== null) {
    next.set(DISCOVERY_PARAM_PLATFORM_CATEGORY, category);
  }

  const radius = state.radiusMeters;
  if (
    typeof radius === 'number' &&
    Number.isInteger(radius) &&
    radius >= DISCOVERY_RADIUS_METERS_MIN &&
    radius <= DISCOVERY_RADIUS_METERS_MAX
  ) {
    next.set(DISCOVERY_PARAM_RADIUS, String(radius));
  }

  const sort = parseDiscoverySort(state.sort);
  if (shouldSerializeSortParam(state, options)) {
    next.set(DISCOVERY_PARAM_SORT, sort);
  }

  // Never serialize intent* — parse remaps legacy deep links to entity; new UI never writes.

  return next;
}

export type DiscoveryFilterIdentity = {
  readonly q: string;
  readonly entity: DiscoveryEntity;
  readonly listKind: DiscoveryListKind;
  readonly platformCategory: string | null;
  readonly radiusMeters: number | null;
  readonly sort: DiscoverySort;
  readonly view: DiscoveryView;
  readonly intentType: DiscoveryIntentType | null;
  readonly intentKey: string | null;
};

/**
 * Filter identity used to drop pagination / list scroll when discovery chrome
 * identity changes (incl. view enter/leave favorites|bag|map|list).
 */
export function discoveryFilterIdentity(
  state: DiscoverySearchParamsState,
): DiscoveryFilterIdentity {
  return {
    q: state.q,
    entity: state.entity,
    listKind: state.view === 'list' ? state.listKind : DISCOVERY_DEFAULT_LIST_KIND,
    platformCategory: state.platformCategory,
    radiusMeters: state.radiusMeters,
    sort: state.sort,
    view: state.view,
    intentType: state.intentType,
    intentKey: state.intentKey,
  };
}

export function discoveryFilterIdentityKey(state: DiscoverySearchParamsState): string {
  const id = discoveryFilterIdentity(state);
  return `${id.q}\0${id.entity}\0${id.listKind}\0${id.platformCategory ?? ''}\0${id.radiusMeters ?? ''}\0${id.sort}\0${id.view}\0${id.intentType ?? ''}\0${id.intentKey ?? ''}`;
}

function applyDiscoveryPatch(
  base: DiscoverySearchParamsState,
  patch: Partial<DiscoverySearchParamsState>,
): DiscoverySearchParamsState {
  const nextQ = patch.q !== undefined ? normalizeDiscoveryQuery(patch.q) : base.q;
  const qChanged = patch.q !== undefined && nextQ !== base.q;

  let intentType: DiscoveryIntentType | null;
  if (qChanged || nextQ.length === 0) {
    intentType = null;
  } else if (patch.intentType !== undefined) {
    intentType = parseDiscoveryIntentType(patch.intentType);
  } else {
    intentType = base.intentType;
  }

  let intentKey: string | null;
  if (qChanged || nextQ.length === 0 || intentType === null) {
    intentKey = null;
  } else if (patch.intentKey !== undefined) {
    intentKey = normalizeDiscoveryIntentKey(patch.intentKey);
  } else {
    intentKey = base.intentKey;
  }

  // D21 / prior-G9-category: platformCategory survives q changes; clear only via clearCategory.
  let platformCategory: string | null;
  if (patch.platformCategory !== undefined) {
    platformCategory = normalizeDiscoveryPlatformCategory(patch.platformCategory);
  } else {
    platformCategory = base.platformCategory;
  }

  const view =
    patch.view !== undefined ? parseDiscoveryView(patch.view) : base.view;

  let listKind: DiscoveryListKind;
  if (view !== 'list') {
    listKind = DISCOVERY_DEFAULT_LIST_KIND;
  } else if (patch.listKind !== undefined) {
    listKind = parseDiscoveryListKind(patch.listKind);
  } else {
    listKind = base.listKind;
  }

  return {
    q: nextQ,
    entity:
      patch.entity !== undefined
        ? parseDiscoveryEntity(patch.entity)
        : base.entity,
    listKind,
    platformCategory,
    radiusMeters:
      patch.radiusMeters !== undefined
        ? parseDiscoveryRadiusMeters(
            patch.radiusMeters === null ? null : String(patch.radiusMeters),
          )
        : base.radiusMeters,
    sort: patch.sort !== undefined ? parseDiscoverySort(patch.sort) : base.sort,
    view,
    intentType,
    intentKey,
  };
}

/**
 * Merge a patch into the current URL without dropping unrelated keys.
 * Always strips cursor + sensitive geo keys + favoritesOnly (cursor is not URL-held).
 */
export function mergeDiscoverySearchParams(
  current: URLSearchParams,
  patch: Partial<DiscoverySearchParamsState>,
): URLSearchParams {
  const parsed = parseDiscoverySearchParams(current);
  const nextState = applyDiscoveryPatch(parsed, patch);
  const sortWasRelevanceInUrl =
    current.has(DISCOVERY_PARAM_SORT) &&
    parseDiscoverySort(current.get(DISCOVERY_PARAM_SORT)) === 'relevance';
  const pinSortRelevance =
    nextState.radiusMeters !== null &&
    nextState.sort === 'relevance' &&
    (patch.sort === 'relevance' || sortWasRelevanceInUrl);
  return serializeDiscoverySearchParams(nextState, {
    preserveFrom: current,
    pinSortRelevance,
  });
}

export function sanitizeDiscoverySearchParams(current: URLSearchParams): URLSearchParams {
  return serializeDiscoverySearchParams(parseDiscoverySearchParams(current), {
    preserveFrom: current,
  });
}

export function discoverySearchParamsNeedSanitize(current: URLSearchParams): boolean {
  for (const key of current.keys()) {
    if (isSensitiveGeoParamKey(key) || isDiscoveryEphemeralParamKey(key)) {
      return true;
    }
  }

  // G14 — any favoritesOnly presence must be remapped / stripped.
  if (current.has(DISCOVERY_PARAM_FAVORITES_ONLY)) {
    return true;
  }

  const qRaw = current.get(DISCOVERY_PARAM_Q);
  if (qRaw !== null && normalizeDiscoveryQuery(qRaw) !== qRaw) {
    return true;
  }

  const categoryRaw = current.get(DISCOVERY_PARAM_PLATFORM_CATEGORY);
  if (
    categoryRaw !== null &&
    normalizeDiscoveryPlatformCategory(categoryRaw) !== categoryRaw
  ) {
    return true;
  }

  const radiusRaw = current.get(DISCOVERY_PARAM_RADIUS);
  if (radiusRaw !== null) {
    const parsed = parseDiscoveryRadiusMeters(radiusRaw);
    if (parsed === null || String(parsed) !== radiusRaw.trim()) {
      return true;
    }
  }

  const sortRaw = current.get(DISCOVERY_PARAM_SORT);
  if (sortRaw !== null) {
    const parsed = parseDiscoverySort(sortRaw);
    if (sortRaw.trim().toLowerCase() !== parsed) {
      return true;
    }
  }

  const viewRaw = current.get(DISCOVERY_PARAM_VIEW);
  if (viewRaw !== null) {
    const parsed = parseDiscoveryView(viewRaw);
    if (viewRaw.trim().toLowerCase() !== parsed) {
      return true;
    }
  }

  const entityRaw = current.get(DISCOVERY_PARAM_ENTITY);
  if (entityRaw !== null) {
    const parsed = parseDiscoveryEntity(entityRaw);
    if (entityRaw.trim().toLowerCase() !== parsed) {
      return true;
    }
  }

  const listKindRaw = current.get(DISCOVERY_PARAM_LIST_KIND);
  if (listKindRaw !== null) {
    const view = parseDiscoveryView(current.get(DISCOVERY_PARAM_VIEW));
    if (view !== 'list') {
      return true;
    }
    const parsed = parseDiscoveryListKind(listKindRaw);
    if (listKindRaw.trim().toLowerCase() !== parsed) {
      return true;
    }
  }

  const intentTypeRaw = current.get(DISCOVERY_PARAM_INTENT_TYPE);
  if (intentTypeRaw !== null) {
    const parsed = parseDiscoveryIntentType(intentTypeRaw);
    if (parsed === null || intentTypeRaw.trim() !== parsed) {
      return true;
    }
  }

  const intentKeyRaw = current.get(DISCOVERY_PARAM_INTENT_KEY);
  if (intentKeyRaw !== null) {
    const parsed = normalizeDiscoveryIntentKey(intentKeyRaw);
    if (parsed === null || intentKeyRaw !== parsed) {
      return true;
    }
  }

  const qForIntent = normalizeDiscoveryQuery(current.get(DISCOVERY_PARAM_Q));
  if (qForIntent.length === 0 && (intentTypeRaw !== null || intentKeyRaw !== null)) {
    return true;
  }

  // Legacy intent still in URL after parse would drop it (productService/category/tenant map).
  if (intentTypeRaw !== null && qForIntent.length > 0) {
    const parsedIntent = parseDiscoveryIntentType(intentTypeRaw);
    if (parsedIntent !== null) {
      return true;
    }
  }

  return false;
}

export function discoverySearchParamsHaveSensitiveGeo(current: URLSearchParams): boolean {
  for (const key of current.keys()) {
    if (isSensitiveGeoParamKey(key)) {
      return true;
    }
  }
  return false;
}
